/**
 * Invoice builder — the admin "Rechnungen" tab.
 *
 * Free-form invoices for e.g. video jobs (NOT tied to a shop order): fill the
 * form on the left, watch the live preview on the right (it's the exact HTML
 * that gets printed), then save as PDF via the browser.
 *
 * Bank details / tax no. / payment terms persist to localStorage so they only
 * ever have to be typed once. The invoice number auto-increments from the last
 * one that was printed.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Printer, ExternalLink, RotateCcw, Eye, EyeOff } from 'lucide-react';
import { buildCustomInvoiceHTML, openCustomInvoice, invoiceTotal } from './invoice';

const SETTINGS_KEY = 'aether_invoice_settings';
const LAST_NR_KEY = 'aether_invoice_last_nr';

const todayISO = () => new Date().toISOString().slice(0, 10);

/** "RE-2026-0007" → "RE-2026-0008". Falls back to a year-based first number. */
function nextInvoiceNumber(last) {
  if (!last) return `RE-${new Date().getFullYear()}-0001`;
  const m = String(last).match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return last;
  return `${m[1]}${String(Number(m[2]) + 1).padStart(m[2].length, '0')}${m[3]}`;
}

const emptyItem = () => ({ description: '', qty: 1, unitPrice: '' });

const loadSettings = () => {
  try { return JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}'); } catch { return {}; }
};

const inputCls =
  'w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-white/25 placeholder:text-white/25';
// Position-row inputs must NOT be w-full — they size via flex-1 / fixed widths.
const posInput =
  'bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-[13px] text-white outline-none focus:border-white/25 placeholder:text-white/25';
const labelCls = 'block font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1.5';
const sectionCls = 'monolith-glass p-4';

function Field({ label, children }) {
  return <div><label className={labelCls}>{label}</label>{children}</div>;
}

export default function InvoiceBuilder() {
  const settings = useMemo(loadSettings, []);

  const [buyer, setBuyer] = useState({ company: '', name: '', street: '', zipCity: '', email: '' });
  const [items, setItems] = useState([emptyItem()]);
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    try { return nextInvoiceNumber(localStorage.getItem(LAST_NR_KEY)); } catch { return nextInvoiceNumber(null); }
  });
  const [invoiceDate, setInvoiceDate] = useState(todayISO);
  const [serviceDate, setServiceDate] = useState('');
  const [note, setNote] = useState('');
  const [paymentTerms, setPaymentTerms] = useState(settings.paymentTerms || 'Zahlbar innerhalb von 14 Tagen ohne Abzug.');
  const [taxNumber, setTaxNumber] = useState(settings.taxNumber || '');
  const [bank, setBank] = useState(settings.bank || { holder: '', iban: '', bic: '' });
  const iframeRef = useRef(null);
  const [showPreview, setShowPreview] = useState(true);

  // Persist the "type once" bits.
  useEffect(() => {
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({ paymentTerms, taxNumber, bank })); } catch { /* ignore */ }
  }, [paymentTerms, taxNumber, bank]);

  const data = { invoiceNumber, invoiceDate, serviceDate, buyer, items, note, paymentTerms, taxNumber, bank };
  const dataKey = JSON.stringify(data);
  const html = useMemo(() => buildCustomInvoiceHTML({ ...JSON.parse(dataKey), embedded: true }), [dataKey]);
  const total = invoiceTotal(items);

  const setItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => (prev.length === 1 ? [emptyItem()] : prev.filter((_, i) => i !== idx)));

  const rememberNumber = () => { try { localStorage.setItem(LAST_NR_KEY, invoiceNumber); } catch { /* ignore */ } };
  const doPrint = () => {
    if (showPreview && iframeRef.current?.contentWindow) {
      try { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print(); } catch { openCustomInvoice(data); }
    } else {
      openCustomInvoice(data); // preview hidden → print from a real tab instead
    }
    rememberNumber();
  };
  const doOpenTab = () => { openCustomInvoice(data); rememberNumber(); };
  const reset = () => {
    setBuyer({ company: '', name: '', street: '', zipCity: '', email: '' });
    setItems([emptyItem()]);
    setNote(''); setServiceDate(''); setInvoiceDate(todayISO());
    try { setInvoiceNumber(nextInvoiceNumber(localStorage.getItem(LAST_NR_KEY))); } catch { /* ignore */ }
  };

  return (
    <div className="w-full space-y-4">
      {/* Top action bar — always visible, also when the preview is hidden */}
      <div className="monolith-glass p-3 flex items-center justify-between gap-2 flex-wrap">
        <button
          onClick={() => setShowPreview((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-[9px] font-mono uppercase tracking-widest transition"
          title="Vorschau ein-/ausblenden"
        >
          {showPreview ? <EyeOff size={12} /> : <Eye size={12} />}
          {showPreview ? 'Vorschau aus' : 'Vorschau an'}
        </button>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-[9px] font-mono uppercase tracking-widest transition" title="Formular leeren">
            <RotateCcw size={11} /> Neu
          </button>
          <button onClick={doOpenTab} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 hover:text-white text-[9px] font-mono uppercase tracking-widest transition" title="In neuem Tab öffnen">
            <ExternalLink size={11} /> Tab
          </button>
          <button onClick={doPrint} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[9px] font-mono uppercase tracking-widest text-black transition hover:scale-[1.03]" style={{ background: 'var(--primary, #F97316)' }}>
            <Printer size={11} /> Als PDF speichern
          </button>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 items-start">
        {/* ---------------- FORM ---------------- */}
        <div className={`w-full ${showPreview ? 'xl:w-[46%] xl:shrink-0' : 'xl:max-w-4xl'} space-y-4`}>
        {/* Empfänger */}
        <div className={sectionCls}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-3">Empfänger</p>
          <div className="space-y-2.5">
            <Field label="Firma (optional)">
              <input value={buyer.company} onChange={(e) => setBuyer({ ...buyer, company: e.target.value })} className={inputCls} placeholder="Muster GmbH" />
            </Field>
            <Field label="Name">
              <input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} className={inputCls} placeholder="Max Mustermann" />
            </Field>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Straße">
                <input value={buyer.street} onChange={(e) => setBuyer({ ...buyer, street: e.target.value })} className={inputCls} placeholder="Musterstr. 1" />
              </Field>
              <Field label="PLZ + Ort">
                <input value={buyer.zipCity} onChange={(e) => setBuyer({ ...buyer, zipCity: e.target.value })} className={inputCls} placeholder="47829 Krefeld" />
              </Field>
            </div>
            <Field label="E-Mail (optional)">
              <input value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} className={inputCls} placeholder="max@example.com" />
            </Field>
          </div>
        </div>

        {/* Rechnungsdaten */}
        <div className={sectionCls}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-3">Rechnung</p>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Rechnungsnr.">
              <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Rechnungsdatum">
              <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className={inputCls} style={{ colorScheme: 'dark' }} />
            </Field>
          </div>
          <div className="mt-2.5">
            <Field label="Leistung / Zeitraum (frei)">
              <input value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} className={inputCls} placeholder="z.B. Juni 2026 oder 05.07.2026" />
            </Field>
          </div>
        </div>

        {/* Positionen */}
        <div className={sectionCls}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">Positionen</p>
            <button onClick={addItem} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 hover:text-white text-[9px] font-mono uppercase tracking-widest transition">
              <Plus size={11} /> Position
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex gap-2 items-center px-1">
              <span className="flex-1 font-mono text-[8px] uppercase tracking-widest text-white/25">Beschreibung</span>
              <span className="w-14 text-center font-mono text-[8px] uppercase tracking-widest text-white/25">Menge</span>
              <span className="w-24 text-right font-mono text-[8px] uppercase tracking-widest text-white/25">Preis €</span>
              <span className="w-[28px] shrink-0" />
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <input
                  value={it.description}
                  onChange={(e) => setItem(idx, { description: e.target.value })}
                  className={`${posInput} flex-1 min-w-0`}
                  placeholder="Beschreibung, z.B. Videoschnitt Imagefilm"
                />
                <input
                  value={it.qty}
                  onChange={(e) => setItem(idx, { qty: e.target.value })}
                  className={`${posInput} w-14 text-center shrink-0`}
                  placeholder="1"
                  inputMode="decimal"
                  title="Menge"
                />
                <input
                  value={it.unitPrice}
                  onChange={(e) => setItem(idx, { unitPrice: e.target.value })}
                  className={`${posInput} w-24 text-right shrink-0`}
                  placeholder="0,00"
                  inputMode="decimal"
                  title="Einzelpreis"
                />
                <button onClick={() => removeItem(idx)} className="p-2 text-white/20 hover:text-red-400 transition shrink-0" title="Position entfernen">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3 pt-3 border-t border-white/5">
            <span className="font-mono text-sm text-white">Gesamt: {total.toFixed(2).replace('.', ',')} €</span>
          </div>
        </div>

        {/* Zahlung (persisted) */}
        <div className={sectionCls}>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mb-1">Zahlung &amp; Anbieter</p>
          <p className="text-[10px] text-white/30 mb-3">Wird gespeichert — musst du nur einmal eintragen.</p>
          <div className="space-y-2.5">
            <Field label="Zahlungsziel">
              <input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className={inputCls} />
            </Field>
            <Field label="Kontoinhaber">
              <input value={bank.holder} onChange={(e) => setBank({ ...bank, holder: e.target.value })} className={inputCls} placeholder="David Ferfers" />
            </Field>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="IBAN">
                <input value={bank.iban} onChange={(e) => setBank({ ...bank, iban: e.target.value })} className={inputCls} placeholder="DE.." />
              </Field>
              <Field label="BIC">
                <input value={bank.bic} onChange={(e) => setBank({ ...bank, bic: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Steuernummer (optional)">
              <input value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} className={inputCls} />
            </Field>
          </div>
        </div>

        {/* Notiz */}
        <div className={sectionCls}>
          <Field label="Notiz auf der Rechnung (optional)">
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className={`${inputCls} resize-none`} placeholder="z.B. Vielen Dank für die gute Zusammenarbeit!" />
          </Field>
        </div>
      </div>

        {/* ---------------- LIVE PREVIEW ---------------- */}
        <div className={showPreview ? 'w-full xl:flex-1 min-w-0 xl:sticky xl:top-6' : 'sr-only'}>
          {showPreview && <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">Vorschau (live)</p>}
          <iframe
            ref={iframeRef}
            srcDoc={html}
            title="Rechnungsvorschau"
            className="w-full rounded-xl border border-white/10 bg-white"
            style={{ height: '78vh', minHeight: 520 }}
          />
        </div>
      </div>
    </div>
  );
}
